import { Select } from '@/components/Select';
import type { EnvironmentView } from '@/api/opsTypes';
import { formatPeriod, recentPeriods } from '@/ops/format';

/**
 * The environment selector for per-environment pages. Billing and messaging live per environment, so the
 * page always says which stack it is reading or changing.
 */
export function EnvironmentPicker({
  env, environments, onChange, label = 'Environment',
}: {
  env: string | null;
  environments: EnvironmentView[];
  onChange: (key: string) => void;
  label?: string;
}) {
  if (environments.length === 0) {
    return <span className="t-label subtle">No environments registered</span>;
  }
  return (
    <Select
      label={label}
      inline
      value={env ?? ''}
      onChange={onChange}
      options={environments.map((e) => ({
        value: e.key,
        label: `${e.label} (${e.key})`,
        hint: e.enabled ? undefined : 'disabled',
      }))}
    />
  );
}

/** A billing period picker (`YYYY-MM`, UTC months) over the last 18 months, newest first. */
export function MonthSelect({
  value, onChange, label = 'Month', months = 18,
}: {
  value: string;
  onChange: (period: string) => void;
  label?: string;
  months?: number;
}) {
  const periods = recentPeriods(months);
  const options = periods.includes(value) ? periods : [value, ...periods];
  return (
    <Select
      label={label}
      inline
      value={value}
      onChange={onChange}
      searchable={false}
      options={options.map((p) => ({ value: p, label: formatPeriod(p), hint: p }))}
    />
  );
}
